import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import { type AssignToCampaignSavedArgs } from 'donor-elf-web/components/pledge/bulk-edit/assign-to-campaign-modal';
import { PledgeRow } from 'donor-elf-web/components/pledges-table';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import {
  type AppliedPropertyFilter,
  ContactIdsIntersection,
  deserializeAppliedFilters,
  type PropertyFilterInfoForQueryFilter,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';
import { type SelectOption } from 'donor-elf-web/lib/form-component-utils';
import numberUtils from 'donor-elf-web/lib/number-utils';
import objectUtils from 'donor-elf-web/lib/object-utils';
import { pledgeFrequencyLabels } from 'donor-elf-web/lib/pledge-utils';
import ContactModel from 'donor-elf-web/models/contact';
import CampaignService from 'donor-elf-web/services/campaign-service';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactsExportService from 'donor-elf-web/services/contacts-export';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import Settings from 'donor-elf-web/services/settings';
import FileSaver from 'file-saver';
import moment from 'moment';
import Papa from 'papaparse';

interface Item {
  pledgeRow: PledgeRow;
  contact?: ContactModel;
}

export default class PledgesIndexController extends Controller {
  @service declare campaignService: CampaignService;
  @service declare contactsCache: ContactsCache;
  @service declare contactsExport: ContactsExportService;
  @service declare formatDateService: FormatDateService;
  @service declare fundGoalService: FundGoalService;
  @service declare settings: Settings;
  @service declare store: Store;

  queryParams = ['filters'];

  @tracked filters?: string;
  @tracked appliedFilters?: AppliedPropertyFilter[];
  @tracked loading = false;
  @tracked monthlyGoal?: number;
  @tracked sortByProperty = 'contact.name';
  @tracked sortByDirection = 'asc';
  @tracked itemsAll: Item[] = [];
  @tracked itemsNoGifts: Item[] = [];
  @tracked itemsOverdue: Item[] = [];
  @tracked itemsGiving: Item[] = [];
  @tracked itemGroupCalculations: any = {};
  @tracked showSections: Boolean = true;
  @tracked showAssignToCampaignModal: boolean = false;

  get showBulkEdit() {
    return this.campaignService.campaignsEnabled && this.itemsAll.length > 0;
  }

  async load() {
    this.loading = true;
    let { availablePropertyFilters, contactsCache } = this;
    let queryFilter: any = {
      includeMetaMonthlyGoal: true,
    };

    for (let appliedFilter of this.appliedFilters || []) {
      let propertyFilter = availablePropertyFilters.find(
        (x) => x.propertyName === appliedFilter.propertyName,
      )!;
      await propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
    }

    let sectionsFilter: string[] | undefined;
    if (queryFilter.sections) {
      sectionsFilter = queryFilter.sections;
      delete queryFilter.sections;
    }

    let response = await this.store.query('pledge', {
      filter: objectUtils.decamelizeKeys(queryFilter),
    });

    if (response.meta?.monthly_goal) {
      this.monthlyGoal = Number(response.meta.monthly_goal);
    } else {
      this.monthlyGoal = undefined;
    }

    let itemsAll: Item[] = [];
    for (let pledge of response.slice()) {
      itemsAll.push({
        pledgeRow: new PledgeRow(pledge),
        contact: contactsCache.contactsById[pledge.contactId!],
      });
    }
    this.itemsAll = itemsAll;
    this.setItemGroups();

    // somewhat of a hack to easily filter by sections
    if (sectionsFilter) {
      let filteredItemsAll: Item[] = [];
      if (sectionsFilter.includes('overdue')) {
        filteredItemsAll = [...filteredItemsAll, ...this.itemsOverdue];
      }
      if (sectionsFilter.includes('noGifts')) {
        filteredItemsAll = [...filteredItemsAll, ...this.itemsNoGifts];
      }
      if (sectionsFilter.includes('giving')) {
        filteredItemsAll = [...filteredItemsAll, ...this.itemsGiving];
      }
      this.itemsAll = filteredItemsAll;
      this.setItemGroups();
    }

    this.setItemGroupCalculations();
    this.loading = false;
  }

  @cached
  get availablePropertyFilters(): PropertyFilterInfoForQueryFilter[] {
    let list: PropertyFilterInfoForQueryFilter[] = [];

    list.push({
      propertyName: 'startDate',
      label: 'Start Date',
      propertyType: 'date',
      availableOperations: ['>='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.startDate = appliedFilter.val;
      },
    });
    list.push({
      propertyName: 'endDate',
      label: 'End Date',
      propertyType: 'date',
      availableOperations: ['<='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.endDate = appliedFilter.val;
      },
    });
    list.push({
      propertyName: 'frequency',
      label: 'Frequency',
      propertyType: 'list',
      availableOperations: ['any'],
      listOptions: this.frequencyOptions,
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.frequency = appliedFilter.val;
      },
    });
    list.push({
      propertyName: 'section',
      label: 'Section',
      propertyType: 'list',
      availableOperations: ['any'],
      listOptions: [
        { val: 'overdue', label: 'Overdue Pledges' },
        { val: 'noGifts', label: 'Pledges without Gifts' },
        { val: 'giving', label: 'Actively Giving Pledges' },
      ],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.sections = appliedFilter.val;
      },
    });
    if (this.campaignService.campaignsEnabled) {
      list.push({
        propertyName: 'campaignId',
        label: 'Campaign',
        propertyType: 'list',
        availableOperations: ['any', 'absent', 'present'],
        listOptions: this.campaignService.sortedCampaignSelectOptions,
        applyToQueryFilter: async (
          appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          if (appliedFilter.operation === 'any') {
            queryFilter.campaignId = appliedFilter.val;
          } else if (appliedFilter.operation === 'absent') {
            queryFilter.campaignId = 'absent';
          } else if (appliedFilter.operation === 'present') {
            queryFilter.campaignId = 'present';
          }
        },
      });
    }
    list.push({
      propertyName: 'removeDuplicatesFromSameContact',
      label: 'Remove Duplicates from Same Contact',
      propertyType: 'no-operation',
      applyToQueryFilter: async (
        _appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.removeDuplicatesFromSameContact = true;
      },
    });
    list.push({
      propertyName: 'affectsGoal',
      label: 'Affects Goal',
      propertyType: 'boolean',
      availableOperations: ['='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        if (appliedFilter.val) {
          queryFilter.affectsGoal = true;
        } else {
          queryFilter.affectsGoal = false;
        }
      },
    });

    let contactFilterInfos =
      this.contactsCache.availablePropertyFiltersForContacts.filter(
        (x) => x.custom.includeOnAnyReport && x.propertyName !== 'campaignIds',
      );
    for (let filterInfo of contactFilterInfos) {
      list.push({
        propertyName: filterInfo.propertyName,
        label: `Contact ${filterInfo.label}`,
        propertyType: filterInfo.propertyType,
        availableOperations: filterInfo.availableOperations,
        listOptions: filterInfo.listOptions,
        listIsSingleSelection: filterInfo.listIsSingleSelection,
        applyToQueryFilter: async (
          appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          let contactIds = (
            await this.contactsCache.filter([appliedFilter])
          ).map((x) => x.id);
          if (contactIds.length === 0) {
            contactIds = ['-1']; // since no records match the contact filter, force the api to return 0 records
          }
          queryFilter.contactIds = ContactIdsIntersection(
            queryFilter.contactIds,
            contactIds,
          );
        },
      });
    }

    return list;
  }

  setItemGroups() {
    let pledgesOverdueDate = this.settings.fundSettings!.pledgesDelinquentDate;
    let itemsNoGifts: Item[] = [];
    let itemsOverdue: Item[] = [];
    let itemsGiving: Item[] = [];

    for (let item of this.itemsAll) {
      let nextDate = item.pledgeRow.pledge.nextExpectedDonationDate;
      // pledges with an end date coming up but no more payments are due will not
      // have a nextExpectedDonationDate
      if (
        !isEmpty(nextDate) &&
        item.pledgeRow.pledge.nextExpectedDonationDate! < pledgesOverdueDate
      ) {
        itemsOverdue.push(item);
      } else {
        if (item.pledgeRow.pledge.hasGifts) {
          itemsGiving.push(item);
        } else {
          itemsNoGifts.push(item);
        }
      }
    }
    this.itemsNoGifts = itemsNoGifts;
    this.itemsOverdue = itemsOverdue;
    this.itemsGiving = itemsGiving;
  }

  setItemGroupCalculations() {
    let { monthlyGoal, isAnnualRollingGoal } = this;
    let calcs: any = {};
    for (let groupName of [
      'itemsAll',
      'itemsNoGifts',
      'itemsOverdue',
      'itemsGiving',
    ]) {
      let items = (this as any)[groupName] as Item[];
      if (items.length) {
        let monthlyAmount = items
          .map((item) => item.pledgeRow.pledge.monthlyAmount)
          .reduce(
            (prevVal: number, curVal: number) =>
              numberUtils.addCurrency(prevVal, curVal),
            0,
          )!;

        let percentOfGoal = monthlyGoal
          ? Math.round((monthlyAmount * 100) / monthlyGoal)
          : 0;
        if (isAnnualRollingGoal) {
          monthlyAmount *= 12;
        }

        calcs[groupName] = {
          monthlyAmount,
          percentOfGoal,
        };
      } else {
        calcs[groupName] = {
          monthlyAmount: 0,
          percentOfGoal: 0,
        };
      }
    }

    this.itemGroupCalculations = calcs;
  }

  handleToggleShowSections = () => {
    this.showSections = !this.showSections;
    this.loading = true;

    setTimeout(() => {
      this.loading = false;
    }, 100);
  };

  handleShowAssignToCampaign = () => {
    this.showAssignToCampaignModal = true;
  };

  templateDidInsert = async () => {
    if (isEmpty(this.filters)) {
      this.filters = serializeAppliedFilters(this.defaultAppliedFilters);
    }
    this.deserializeFilters();
    await this.load();
  };

  templateWillDestroy = () => {
    this.loading = true;
    this.filters = undefined;
    this.appliedFilters = undefined;
    this.itemsAll = [];
  };

  get actionsMenuItems() {
    return [
      {
        label: 'Download',
        svgJar: 'download',
        onClick: this.download,
      },
    ];
  }

  get defaultAppliedFilters(): AppliedPropertyFilter[] {
    return [
      {
        propertyName: 'startDate',
        operation: '>=',
        val: moment().startOf('month').format('YYYY-MM-DD'),
      },
      {
        propertyName: 'endDate',
        operation: '<=',
        val: moment().add(11, 'months').endOf('month').format('YYYY-MM-DD'),
      },
    ];
  }

  handleFiltersChanged = async (currentFilters: AppliedPropertyFilter[]) => {
    this.appliedFilters = currentFilters;
    this.serializeFilters();
    await this.load();
  };

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
  }

  get frequencyOptions(): SelectOption[] {
    let options: SelectOption[] = [];
    for (let frequencyId in pledgeFrequencyLabels) {
      if (pledgeFrequencyLabels[frequencyId] !== 'Weekly') {
        options.push({
          val: frequencyId,
          label: pledgeFrequencyLabels[frequencyId],
        });
      }
    }
    return options;
  }

  get excludeNoGiftsFromGoal() {
    return this.settings.fundSettings!.lookingForwardExcludePledgesNoGifts;
  }
  get excludeOverdueFromGoal() {
    return this.settings.fundSettings!.lookingForwardExcludePledgesDelinquent;
  }
  get isAnnualRollingGoal() {
    return this.settings.userSettings!.showAnnualGoal;
  }

  get selectedPledges() {
    return this.itemsAll
      .filter((i) => i.pledgeRow.selected)
      .map((i) => i.pledgeRow.pledge);
  }

  // Don't show monthly summary for YTD goal types because it's confusing
  get showSummaryCard() {
    return !this.fundGoalService.hasYtdGoalType;
  }

  get sortBy() {
    return [`${this.sortByProperty}:${this.sortByDirection}`];
  }

  get sortedItemsAll() {
    return sortByProperties(this.itemsAll, this.sortBy);
  }

  get sortedItemsNoGifts() {
    return sortByProperties(this.itemsNoGifts, this.sortBy);
  }

  get sortedItemsOverdue() {
    return sortByProperties(this.itemsOverdue, this.sortBy);
  }

  get sortedItemsGiving() {
    return sortByProperties(this.itemsGiving, this.sortBy);
  }

  get overduePledgesInDanger() {
    return (
      this.excludeOverdueFromGoal &&
      (this.itemGroupCalculations?.itemsOverdue?.monthlyAmount || 0) > 0
    );
  }

  get noGiftsPledgesInDanger() {
    return (
      this.excludeNoGiftsFromGoal &&
      (this.itemGroupCalculations?.itemsNoGifts?.monthlyAmount || 0) > 0
    );
  }

  get totalAmount() {
    return this.itemsAll
      .map((i) => i.pledgeRow.pledge.amount)
      .reduce(
        (prevVal, curVal) => numberUtils.addCurrency(prevVal!, curVal || 0),
        0,
      );
  }

  handleSortBy = (property: string) => {
    let currentSortByProperty = this.sortByProperty;
    if (currentSortByProperty === property) {
      if (this.sortByDirection === 'asc') {
        this.sortByDirection = 'desc';
      } else {
        this.sortByDirection = 'asc';
      }
    } else {
      this.sortByProperty = property;
      this.sortByDirection = 'asc';
    }
  };

  handleSavedAssignToCampaign = (args: AssignToCampaignSavedArgs) => {
    for (let pledge of args.reloadedPledges) {
      let item = this.itemsAll.find((i) => i.pledgeRow.pledge.id === pledge.id);

      if (item) {
        item.pledgeRow.pledge = pledge;
      }
    }

    this.showAssignToCampaignModal = false;
  };

  handleCloseAssignToCampaign = () => {
    this.showAssignToCampaignModal = false;
  };

  download = async () => {
    let { formatDateService } = this;
    let rows = [];
    let headerRow = [
      'Group',
      'Contact',
      'Start Date',
      'End Date',
      'Frequency',
      'Amount',
      'Monthly Amount',
      'Annual Amount',
      'Last Gift',
      'Next Gift',
      'Days Overdue',
      'Notes',
      '---',
    ];
    let headerInfo = await this.contactsExport.addContactsHeaderColumns(
      headerRow,
      this.contactsCache.contacts,
    );
    rows.push(headerRow);
    const today = moment();
    let createRow = async (item: Item, groupName: string) => {
      const pledge = item.pledgeRow.pledge;
      let daysOverdue = today.diff(pledge.nextExpectedDonationDate, 'days');
      if (pledge.nextExpectedDonationDate) {
        if (daysOverdue < 0) {
          daysOverdue = 0;
        }
      } else {
        daysOverdue = null;
      }

      let row = [
        groupName,
        item.contact?.name,
        formatDateService.formatForFile(item.pledgeRow.pledge.startDate),
        formatDateService.formatForFile(item.pledgeRow.pledge.endDate),
        item.pledgeRow.pledge.frequencyLabel,
        item.pledgeRow.pledge.amount,
        item.pledgeRow.pledge.monthlyAmount,
        item.pledgeRow.pledge.annualAmount,
        formatDateService.formatForFile(item.pledgeRow.pledge.lastDonationDate),
        formatDateService.formatForFile(
          item.pledgeRow.pledge.nextExpectedDonationDate,
        ),
        daysOverdue,
        item.pledgeRow.pledge.notes,
        '',
      ];
      await this.contactsExport.addContactInfoToRow(
        row,
        item.contact,
        headerInfo,
      );
      return row;
    };

    for (let item of this.sortedItemsOverdue) {
      rows.push(await createRow(item, 'Overdue'));
    }
    for (let item of this.sortedItemsNoGifts) {
      rows.push(await createRow(item, 'Without Gifts'));
    }
    for (let item of this.sortedItemsGiving) {
      rows.push(await createRow(item, 'Actively Giving'));
    }

    await this.store.createRecord('contact-download').save();

    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'pledges.csv', true);
  };
}
